import * as React from 'react';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { NotificationImportantRounded } from '@mui/icons-material';

import Layout from '../Layout/Layout';
import CompanyCertificationAdminPanel from './CompanyCertificationAdminPanel';
import ApplicationAdminPanel from './ApplicationAdminPanel';
import './adminStyles.css';
import AssessmentAdminPanel from './AssessmentAdminPanel';
import AssessorAdminPanel from './AssessorAdminPanel';
import { getAssessmentSurveyAssessmentsForDataGrid } from '../../services/assessmentSurveyAssessment';
import PeopleIcon from '@mui/icons-material/People';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AppsIcon from '@mui/icons-material/Apps';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import PolicyIcon from '@mui/icons-material/Policy';
import ScreeningAdminPanel from './ScreeningAdminPanel';
import CertificationsAdminPanel from './CertificationsAdminPanel';
import UsersAdminPanel from './UsersAdminPanel';
import BillingRecordsAdminPanel from './BillingRecordsAdminPanel';
import RemediationVerificationAdminPanel from './RemediationVerificationAdminPanel';
import WorkflowDisplayStatusVerification from './WorkflowDisplayStatusVerification';
import FeatureFlagsAdminPanel from './FeatureFlagsAdminPanel';
import { useAppSelector } from '../../app/hooks';
import { USER_TYPES } from '../../services/user';
import { LABELS } from '../../utils/labels';
import PartnerResourcesAdminPanel from './PartnerResourcesAdminPanel';
import QuestionnairesAdminPanel from './QuestionnairesAdminPanel';
import SubNavColumn, { SubNavItem } from '../Navigation/SubNavColumn';
import { useProfileVersion } from '../Vendors/profileVersion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

const AdminShell = styled('div')({ display: 'flex', flex: 1, minHeight: 0 });
const AdminContent = styled('div')({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  padding: 24,
  // The white Card already frames each panel; drop the DataGrid's own border.
  '& .MuiDataGrid-root': { border: 'none' },
});
const AdminHeaderRoot = styled('div')({ display: 'flex', gap: 12, alignItems: 'center', padding: '16px 16px 14px' });
const AdminAvatar = styled('div')({ width: 44, height: 44, borderRadius: 8, background: '#ede7f3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a4c93' });
const AdminName = styled('div')({ fontWeight: 700, fontSize: 14, color: '#1c2a42' });
const AdminSubtitle = styled('div')({ fontSize: 12, color: '#8a94a3' });
const AdminTitle = styled('h2')({ margin: '0 0 16px', fontSize: 20, fontWeight: 300, color: '#1c2a42' });

const ADMIN_TABS = [
  { key: 'users', label: 'Users', icon: PeopleIcon },
  { key: 'company-certifications', label: 'Company Certifications', icon: WorkspacePremiumIcon },
  { key: 'applications', label: 'Applications', icon: AppsIcon },
  { key: 'assessments', label: 'Assessments', icon: AssignmentIcon },
  { key: 'assessors', label: 'Assessors', icon: HowToRegIcon },
  { key: 'certifications', label: 'Certifications', icon: VerifiedIcon },
  { key: 'billing-records', label: 'Billing Records', icon: DescriptionIcon },
  { key: 'remediation-validation', label: LABELS.remediationValidation, icon: FactCheckIcon },
  { key: 'questionnaires', label: 'Questionnaires', icon: DynamicFormIcon },
  { key: 'partner-resources', label: 'Partner Resources', icon: MenuBookIcon },
  { key: 'certificate-screening', label: 'Certificate Screening', icon: PolicyIcon },
  { key: 'display-status-verification', label: 'Display Status Verification', icon: VisibilityIcon },
  { key: 'feature-flags', label: 'Feature Flags', icon: ToggleOnIcon },
];

const DEFAULT_TAB = ADMIN_TABS[0].key;
const DEFAULT_CATEGORY = 'key-terms';

function renderPanel(key: string, onAssessmentsAddressed: () => void) {
  switch (key) {
    case 'company-certifications': return <CompanyCertificationAdminPanel />;
    case 'applications': return <ApplicationAdminPanel />;
    case 'assessments': return <AssessmentAdminPanel assessmentsAddressed={onAssessmentsAddressed} />;
    case 'assessors': return <AssessorAdminPanel />;
    case 'certifications': return <CertificationsAdminPanel />;
    case 'billing-records': return <BillingRecordsAdminPanel />;
    case 'remediation-validation': return <RemediationVerificationAdminPanel />;
    case 'partner-resources': return <PartnerResourcesAdminPanel />;
    case 'certificate-screening': return <ScreeningAdminPanel />;
    case 'display-status-verification': return <WorkflowDisplayStatusVerification />;
    case 'questionnaires': return <QuestionnairesAdminPanel />;
    case 'feature-flags': return <FeatureFlagsAdminPanel />;
    case 'users':
    default: return <UsersAdminPanel />;
  }
}

export default function AdminTabs() {
  const [assessmentsAlert, setAssessmentsAlert] = useState(false);

  const { tab } = useParams<{ tab?: string }>();
  const { user } = useAppSelector(state => state.user);
  const { showV2 } = useProfileVersion();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const category = searchParams.get('category');
  const adminSection = tab && ADMIN_TABS.some(t => t.key === tab) ? tab : DEFAULT_TAB;

  const handleAssessmentsAddressed = () => setAssessmentsAlert(false);

  useEffect(() => {
    if (user?.type !== USER_TYPES.TPN_ADMIN) return;

    getAssessmentSurveyAssessmentsForDataGrid({ page: 0, pageSize: 1 }).then((response) => {
      if (response.results.length > 0) setAssessmentsAlert(true);
    });
  }, [user?.type]);

  // Keep the URL canonical: default section, and the ?category= default for
  // Partner Resources (unchanged from the top-tab version). Questionnaires owns
  // its own deeper drill-down paths, so leave its URL alone.
  useEffect(() => {
    if (adminSection === 'questionnaires') return;
    const urlCategory =
      adminSection === 'partner-resources'
        ? `?category=${category ?? DEFAULT_CATEGORY}`
        : '';
    navigate(`/admin/${adminSection}${urlCategory}`);
  }, [adminSection, category, navigate]);

  if (user?.type !== USER_TYPES.TPN_ADMIN) return null;

  // V1 profile: the original top-tab strip (nav-only change from v2). Same 11
  // panels, driven by the same renderPanel; only the navigation chrome differs.
  if (!showV2) {
    const activeIndex = ADMIN_TABS.findIndex((t) => t.key === adminSection);
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) =>
      navigate(`/admin/${ADMIN_TABS[newValue].key}`);

    return (
      <Layout pageTitle="Admins" includeHeader={false} includeDivider={false}>
        <Tabs variant="scrollable" value={activeIndex} onChange={handleTabChange} aria-label="Company Admin Area">
          {ADMIN_TABS.map((t, idx) => (
            <Tab
              key={t.key}
              label={t.label}
              id={`admin-tab-${idx}`}
              aria-controls={`admin-tabpanel-${idx}`}
              {...(t.key === 'assessments'
                ? {
                  sx: { minHeight: '48px' },
                  iconPosition: 'start' as const,
                  icon: (
                    <>
                      {assessmentsAlert && (
                        <NotificationImportantRounded
                          color="warning"
                          sx={{ position: 'absolute', top: '5px', left: '0', width: '.8em' }}
                        />
                      )}
                    </>
                  ),
                }
                : null)}
            />
          ))}
        </Tabs>
        <Box sx={{ p: 3 }}>{renderPanel(adminSection, handleAssessmentsAddressed)}</Box>
      </Layout>
    );
  }

  const items: SubNavItem[] = ADMIN_TABS.map((t) => ({
    id: t.key,
    label: t.label,
    icon: t.icon,
    to: `/admin/${t.key}`,
    alert: t.key === 'assessments' && assessmentsAlert,
  }));

  const adminHeader = (
    <AdminHeaderRoot>
      <AdminAvatar>
        <AdminPanelSettingsIcon />
      </AdminAvatar>
      <div>
        <AdminName>Admin</AdminName>
        <AdminSubtitle>TPN Administration</AdminSubtitle>
      </div>
    </AdminHeaderRoot>
  );

  return (
    <Layout fullBleed pageTitle="Admins">
      <AdminShell>
        <SubNavColumn header={adminHeader} items={items} activeId={adminSection} ariaLabel="Admin sections" />
        <AdminContent>
          <AdminTitle>{ADMIN_TABS.find((t) => t.key === adminSection)?.label}</AdminTitle>
          {renderPanel(adminSection, handleAssessmentsAddressed)}
        </AdminContent>
      </AdminShell>
    </Layout>
  );
}
